import React, { useState } from 'react';
import { FormikProps, Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import * as Yup from 'yup';
import { Row, Col } from 'antd';

import { ResourceDownloadResponse } from 'api/adminApiClient';
import FormikEffect from 'components/formik/FormikEffect';
import FormItem from 'components/formik/FormItem';
import CertificateUploader from './CertificateUploader';
import Radio from 'components/formik/Radio';
import { mediumCharactersLength } from 'constants/index';
import { RadioChangeEvent } from 'antd/lib/radio';
import SOWTemplateUploader from './SOWTemplateUploader';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<GeneralContractInfoFormValues>) => void;
  certificateFileInfo?: ResourceDownloadResponse;
  initialValues?: GeneralContractInfoFormValues;
  sowTemplateFileInfo?: ResourceDownloadResponse;
};

export interface GeneralContractInfoFormValues {
  specifications: string;
  submittedTo: string;
  certificate?: string[];
  isCreditCheckVerified?: boolean;
  sowRequired?: boolean;
  sowSpecialInstructions: string;
  sowTemplate?: string[];
}

const validationSchema = Yup.object({
  specifications: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Certificate of insurance specifications should be ${mediumCharactersLength} characters maximum`,
    ),
  submittedTo: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Submitted to should be ${mediumCharactersLength} characters maximum`,
    ),
  certificate: Yup.array().of(Yup.string()),
  isCreditCheckVerified: Yup.boolean(),
  sowRequired: Yup.boolean(),
  sowSpecialInstructions: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `SOW special instructions should be ${mediumCharactersLength} characters maximum`,
    ),
  sowTemplate: Yup.array().of(Yup.string()),
});

export default function GeneralContractInfo(props: Props) {
  const {
    formRef,
    initialValues: predefinedInitialValues,
    certificateFileInfo,
    onChange,
    sowTemplateFileInfo,
  } = props;
  const [sowTemplateFile, setSowTemplateFile] = useState(sowTemplateFileInfo);

  const handleSowRequiredChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<GeneralContractInfoFormValues>,
  ) => {
    if (!ev.target.value) {
      formik.setFieldValue('sowTemplate', '', false);
      formik.setFieldValue('sowSpecialInstructions', '', false);
    }

    setSowTemplateFile(undefined);
  };

  const initialValues: GeneralContractInfoFormValues = {
    specifications: '',
    submittedTo: '',
    certificate: [],
    isCreditCheckVerified: undefined,
    sowRequired: undefined,
    sowSpecialInstructions: '',
    sowTemplate: [],
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-title">Insurance requirements</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="specifications"
                  label="Certificate of insurance specifications"
                  data-id="editCustomerInfoSpecificationsField"
                >
                  <Input
                    name="specifications"
                    placeholder="Enter certificate of insurance specifications"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="submittedTo"
                  label="Submitted to"
                  data-id="editCustomerInfoSubmittedToField"
                >
                  <Input
                    name="submittedTo"
                    placeholder="Enter submitted to"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <div className="upload-wrapper">
                  <FormItem
                    name="certificate"
                    label="Certificate of insurance created"
                    data-id="editCustomerInfoCertificateField"
                    showValidateError={false}
                  >
                    <CertificateUploader
                      fieldName="certificate"
                      file={certificateFileInfo}
                    />
                  </FormItem>
                </div>
              </Col>

              <Col xs={12} md={12}>
                <FormItem
                  name="isCreditCheckVerified"
                  label="Credit check verified"
                  data-id="editCustomerIsCreditCheckVerifiedField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="isCreditCheckVerified"
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="sowRequired"
                  label="SOW required"
                  data-id="editCustomerSOWRequiredField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="sowRequired"
                    onChange={(ev) => handleSowRequiredChange(ev, formik)}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              {formik.values.sowRequired && (
                <>
                  <Col xs={24} md={12}>
                    <FormItem
                      name="sowSpecialInstructions"
                      label="SOW special instructions"
                      data-id="editCustomerInfoSowSpecialInstructionsField"
                    >
                      <Input
                        name="sowSpecialInstructions"
                        placeholder="Enter SOW special instructions"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="upload-wrapper">
                      <FormItem
                        name="sowTemplate"
                        label="SOW template"
                        data-id="editCustomerSOWTemplateField"
                        showValidateError={false}
                      >
                        <SOWTemplateUploader
                          fieldName="sowTemplate"
                          file={sowTemplateFile}
                        />
                      </FormItem>
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        </>
      )}
    </Formik>
  );
}
